<template>
  <section class="w-full h-full">
    <!--=====================================
      =              TabSwitcher             =
      ======================================-->
    <div class="w-full h-full bg-white text-center pt-8 pb-3" v-if="!isExpiringIntegratedUser && !$route.params.plan">
      <p class="w-auto mx-auto ds-text-base text-gray-700 font-medium">Let's find the right fit for you!</p>
    </div>
    
    <TabSwitcher
      @tab-change="handleTabChange"
      v-model="activeTab"
      :tabs="tabs"
      v-if="!$route.params.plan && canViewInsights"
    />
    
    <!--=====================================
    =           Essentials and Insights     =
    ======================================-->
    <transition name="fade" appear mode="out-in">
      <keep-alive>
        <router-view @hasMounted="() => (hasMounted = true)" name="options" />
      </keep-alive>
    </transition>

    <template v-if="!$route.params.plan && hasMounted">
      <!-- =============================================
    =           Testimonials and FAQ              =
    ============================================= -->
      <div class="max-w-1200 mx-auto my-6 flex flex-col xl:flex-row">
        <!-- Schedule Call Section  -->
        <div class="xl:w-2/5 xl:pr-3 lg:pb-0 m-2 lg:m-3 xl:mx-0">
          <div
            class="bg-white rounded-lg h-full p-4 flex flex-col sm:flex-row xl:flex-col"
          >
            <img
              src="https://cdn.sportsrecruits.com/assets/athlete-membership-solutions/sr-team-3.jpg"
              class="object-cover mb-4 sm:mb-0 xl:mb-4 w-full sm:w-1/3 xl:w-full"
              style="border-radius: 8px"
            />
            <div
              class="sm:w-2/3 xl:w-full ml-0 sm:ml-4 xl:ml-0 flex flex-col h-auto xl:h-full"
            >
              <p
                class="ds-text-3xl md:ds-text-4xl text-gray-1000 font-bold mb-6 flex-1"
              >
                We’re here to help. Schedule a call to discuss your family’s
                needs.
              </p>

              <div class="w-full">
                <Button
                  text="Schedule a Call"
                  emphasis="medium"
                  color="blue"
                  @click="
                    scheduleCall(
                      'https://calendly.com/sr-insights/sportsrecruits-memberships-learn-more',
                      {
                        event: 'clicked_schedule_call',
                        cta_location: 'schedule_call',
                      }
                    )
                  "
                  class="inline-flex"
                />
              </div>
            </div>
          </div>
        </div>
        <!-- Testimonial Carousel -->
        <carousel v-model="activeTestimony" class="xl:w-3/5 m-2 lg:m-3 xl:mx-0">
          <div
            v-for="(testimony, i) in testimonials"
            :key="i"
            class="ds-text-2xl font-bold text-gray-1000 bg-white w-full rounded-lg overflow-hidden p-4 relative flex flex-col"
          >
            <div class="flex-1">
              <img
                src="https://cdn.sportsrecruits.com/assets/itg/quote-mark.svg"
                class="absolute left-0 top-0 w-24 h-24"
              />

              <p
                class="ds-text-3xl md:ds-text-4xl text-gray-1000"
                style="margin-top: 5rem"
                v-html="testimony.content"
              ></p>
            </div>
            <div class="flex flex-col w-3/4 sm:w-full">
              <div class="flex items-center mt-6 mb-4">
                <!-- <UserAvatar :size="64" :image-url="testimony.user.avatar" /> -->

                <div
                  :style="`background: url(${testimony.user.avatar}) no-repeat; background-size: cover; background-position: top center;`"
                  class="rounded-full shadow h-16 w-16 border border-solid border-gray-100 z-10 bg-white"
                />
                <div
                  :style="`background: url(${testimony.college.logo}) no-repeat; background-size: 48px 48px; background-position: center center;`"
                  class="rounded-full shadow h-16 w-16 border border-solid border-gray-100 z-10 -ml-2 bg-white"
                />
              </div>

              <p class="text-gray-900 text-sm ds-text-sm font-semibold">
                {{ testimony.user.title }}
              </p>

              <p class="text-gray-900 text-sm ds-text-sm font-medium">
                Committed to {{ testimony.college.name }}, {{ testimony.sport }}
              </p>
            </div>
          </div>
        </carousel>
      </div>

      <!-- Guidance CTA  -->
      <div id="guidance-cta" class="mx-2 lg:mx-3 xl:mx-0 pb-8 cursor-pointer" v-if="activeTab === 0 && !isExpiringIntegratedUser">
        <div class="bg-blue-300 text-white p-6 lg:p-8 max-w-1200 mx-auto rounded-lg" @click="handleTabChange(tabs[1])">
            <div class="flex items-center">
              <div class="mr-8 hidden lg:block"><img src="https://cdn.sportsrecruits.com/assets/itg/dedicated-guidance.svg" alt="">
              </div>
              <div class="flex flex-col">
                <h4 class="m-0 p-0 mb-3 ds-text-4xl font-bold">
                  Looking for more guidance in your recruiting process?
                </h4>
                <p class="mb-6 ds-text-base font-normal">
                  If you are looking for guidance and support throughout your recruiting process check out our Recruiting Guidance Plans which include a lifetime Pro account.
                </p>
                <span class="font-medium ds-text-base text-white cursor-pointer select-none">See Recruiting Guidance Options <i class="sr-2 sr-v2-arrow-right"></i></span>
              </div>
            </div>

        </div>
      </div>

      <div class="w-full bg-gray-1000 pb-16">
        <div class="max-w-1200 mx-auto">
          <div class="text-white px-4 py-8">
            <p
              class="text-white text-3xl ds-text-3xl font-bold text-center mb-6"
            >
              Frequently Asked Questions
            </p>

            <accordion
              v-model="activeFaq"
              :sections="faqs"
              class="divide-solid divide-y divide-white mb-6 mx-auto"
              header-class="first:border-t last:border-b border-l-0 border-r-0 border-white"
              style="max-width: 768px"
              @sectionOpened="trackFaq"
            ></accordion>
          </div>
        </div>
      </div>

      <ScheduleCall
        class="fixed bottom-0"
        @click="
          scheduleCall(
            'https://calendly.com/sr-insights/sportsrecruits-memberships-learn-more',
            {
              event: 'clicked_schedule_call',
              cta_location: 'schedule_call_bottom',
            }
          )
        "
      />
    </template>
  </section>
</template>

<script>
import tracking from "./utils/tracking";
import TabSwitcher from "./components/TabSwitcher";
import ScheduleCall from "@/components/Pricing/components/PricingScheduleCall";
import Accordion from "@/components/Pricing/components/Accordion";
import Carousel from "@/components/Pricing/components/Carousel";
import UserAvatar from "@/components/UserAvatar";
import { Button } from '@sportsrecruits/playbook';
import { mapState } from "vuex";

export default {
  name: "Pricing",
  components: {
    Carousel,
    Accordion,
    TabSwitcher,
    ScheduleCall,
    UserAvatar,
    Button,
  },
  data() {
    return {
      tabs: [
        {
          id: 0,
          shortLabel: "Pro Account",
          label: "Pro Account",
          route: "Pricing/Essentials",
        },
        {
          id: 1,
          shortLabel: "Guidance",
          label: "Recruiting Guidance",
          route: "Pricing/Insights",
        },
      ],
      activeComponent: null,
      activeFaq: null,
      activeTestimony: null,
      canViewInsights: true,
      isExpiringIntegratedUser: null,
      hasMounted: false,
    };
  },
  computed: {
    ...mapState("pricing", {
      activeTab: (state) => state.activeTab,
      products: (state) => state.products,
      featureGroups: (state) => state.featureGroups,
      testimonials: (state) => state.testimonials,
      faqs: (state) => state.faqs,
    }),
  },
  watch: {
    activeTestimony(index) {
      let testimony = this.$store.getters["pricing/getTestimony"](index);

      if (testimony) {
        this.trackTestimony(testimony);
      }
    },
  },
  methods: {
    handleTabChange(tab) {
      if (this.$route.name !== tab.route) {
        this.$router.push({ name: tab.route });
      }
    },
    scheduleCall(calendlyLink, segmentData) {
      tracking.calendly(calendlyLink, segmentData);
    },
    trackFaq(index) {
      let faq = this.$store.getters["pricing/getFaq"](index);

      if (faq) {
        tracking.faq({
          event: "clicked_faq",
          cta_location: faq.label,
        });
      }
    },
    trackTestimony(testimony) {
      tracking.testimony({
        event: "clicked_testimonial_nav",
        cta_location: testimony.user.title,
      });
    },
  },
  beforeMount() {
    this.$store.commit("pricing/setProducts", window.pricing.products);
    this.$store.commit(
      "pricing/setFeatureGroups",
      window.pricing.featureGroups
    );
    this.$store.commit("pricing/setTestimonials", window.pricing.testimonials);
    this.$store.commit("pricing/setFaqs", window.pricing.faqs.filter(faq => faq));
    this.$store.commit("pricing/setPlans", window.pricing.plans);
    this.$store.commit("pricing/setProFeatures", window.pricing.proFeatures);
    
    this.$store.commit("pricing/setGender", window.pricing.gender);
    this.$store.commit("pricing/setSport", window.pricing.sport);

    this.canViewInsights = window.pricing.canViewInsights;
    this.isExpiringIntegratedUser = window.pricing.isExpiringIntegratedUser;
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll, false);
    this.$nextTick(()=> {
      window.clickEssentials = () => {
        if (!['/pricing/essentials', '/pricing'].includes(this.$route.path)) {
          document.querySelector('.js-essentials-tab').click();
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth'});
        }
      };
      window.clickGuidance =  () => {
        if (this.$route.path !== '/pricing/insights') {
          var guidanceTab = document.getElementsByClassName('js-guidance-tab');
          if (guidanceTab.length > 0) {
            document.querySelector('.js-guidance-tab').click();
          } else {
            window.scrollTo({ top: 0, behavior: 'smooth'});
          }
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth'});
        }
      };
    })
  },
};
</script>

<style scoped lang="scss">
/deep/ .max-w-1200 {
  max-width: 1200px;
}

/deep/ .custom-grid-columns {
  grid-template-columns: minmax(0px, 302px) repeat(3, minmax(0, 1fr));
}

@media screen and (max-width: 1023px) {
  /deep/ .custom-grid-columns {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media screen and (max-width: 767px) {
  /deep/ .custom-grid-columns {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }
}

/deep/ .lg-header {
  font-size: 60px;
  line-height: 64px;
}

/deep/ .bg-half-white {
  background-image: linear-gradient(
    to bottom,
    white 50%,
    rgba(255, 255, 255, 0) 50%
  );
}
</style>
